"use client";

import { c as _c } from "react/compiler-runtime";
import { SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprNetworkConfirmationButton } from "@/configuration/groups/tpr/components/network-confirmation-button";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprReceivingAssignmentEditor } from "@/configuration/groups/tpr/components/receiving-assignment-editor";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import { refreshTprWorkflowProgress, useTprLiveWorkflowRefresh } from "@/configuration/groups/tpr/components/workflow-progress";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import { describeTprSpecialtyCategories, matchTprDiagnosisSpecialties } from "@/configuration/groups/tpr/workflows/physician-roster";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry, TprSyncChecklistUrgency } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaCoverageResolutionDialog } from "@/src/components/tasks/athena-coverage-resolution-dialog";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { useTaskStepVerifications, useApproveTaskMutation, useLocalTaskUpdateMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
import { resolveTaskStepPhase, TaskStepPhase, type PinnableTaskStepPhase, type TaskStepCardState } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";
const NOT_AVAILABLE = "N/A";

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = TaskStepCardState;

/**
 * Stable identity for each TPR checklist step, used as `task_step_verification.step_key`
 * so recorded accuracy stays comparable when steps are renumbered, retitled, or reordered. Never
 * derive this from `number` or `title` — both change; these slugs must not.
 */
export const TPR_TASK_STEP_KEY = {
  LOCATE_DOCUMENT: "locate_document",
  PROVIDER_LOCATION: "provider_location",
  DEMOGRAPHICS_CARE_TEAM: "demographics_care_team",
  INSURANCE: "insurance",
  REFERRAL_AUTHORIZATION: "referral_authorization",
  OUTBOUND_FAXES: "outbound_faxes",
  FULL_REGISTRATION: "full_registration",
  PROCESS_REFERRAL_DOCUMENT: "process_referral_document",
  DOCUMENT_UPLOAD: "document_upload",
  QUICKVIEW: "quickview",
  BUCKET_ASSIGNMENT: "bucket_assignment"
} as const;
export type TprTaskStepKey = (typeof TPR_TASK_STEP_KEY)[keyof typeof TPR_TASK_STEP_KEY];
type TimelineStep = {
  number: number;
  title: string;
  stepKey: TprTaskStepKey;
  status: TimelineStatus;
  /** See `TPR_PINNED_STEP_PHASE`. Omitted means the phase follows the status. */
  phase?: PinnableTaskStepPhase;
  /** Why this step needs a human, in place of the card's generic instruction. */
  detail?: string;
  summary: string;
  workflowStepKeys: StepKey[];
  /**
   * The few fields worth seeing on a card that is still waiting its turn — what automation
   * decided, without the evidence behind it. Items with no value are dropped, so a step the
   * workflow hasn't reached shows nothing rather than a row of N/A.
   */
  preview?: DecisionMetadataItem[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control?: React.ReactNode;
  };
};

/**
 * Steps whose phase is a property of the step itself, not of how cleanly automation happened to
 * resolve it on this referral.
 *
 * These are the TPR steps automation was never built to finish: outbound faxes are still
 * partially manual, the referral document needs a human to key parts of it into Athena, and the
 * bucket is ops' judgement call. They stay `ACTION_NEEDED` even when every workflow step under
 * them completed, because "completed" there only means the automated part is done — the human
 * part still isn't.
 *
 * Referral Authorization is pinned per-referral rather than here: it's `ACTION_NEEDED` only when
 * it needs an outbound fax, and plain `QA` when adding the authorization object is the whole job.
 *
 * Every other step is automated end to end, so it falls through to its status: `QA` when it
 * worked, `FAILED` when it didn't. A failed sync outranks a pin — see `resolveTaskStepPhase`.
 */
const TPR_PINNED_STEP_PHASE: Partial<Record<TprTaskStepKey, PinnableTaskStepPhase>> = {
  [TPR_TASK_STEP_KEY.OUTBOUND_FAXES]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT]: TaskStepPhase.ACTION_NEEDED
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};
export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  referralDate: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  /** Null when the patient genuinely has no secondary plan, as opposed to an uncaptured one. */
  secondaryPlanLabel: string | null;
  secondarySubscriberId: string | null;
  isSecondarySpecialCaseInsurance: boolean;
  /**
   * The authorization terms as extracted from the referral. Ops needs the whole set — a number with
   * no validity window or visit count can't be checked against what Athena holds.
   */
  authorizationNumber: string;
  authorizationStatus: string;
  authorizationStartDate: string;
  authorizationEndDate: string;
  authorizationVisits: string;
  /** Separate from `authorizationVisits`: "services" and "visits" are distinct extracted fields. */
  authorizationServices: string | null;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  diagnosis: string;
  /** Which of TPR's four sub-specialty categories the diagnosis text maps to (General /
   * Interventional / Structural / EP) — the bucket the provider resolver matched providers against. */
  diagnosisCategories: string;
};

/**
 * The condensed body of a card that hasn't come up yet.
 *
 * Drops items with no value, so a step the workflow genuinely hasn't reached renders nothing
 * instead of a row of N/A — the point is to show what was decided, and nothing has been yet.
 */
function TimelineStepPreview(t0) {
  const $ = _c(6);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items ?? [];
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.filter(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resolved = t2;
  let t3;
  if ($[4] !== resolved) {
    t3 = resolved.length > 0 ? <DecisionMetadata items={resolved} /> : null;
    $[4] = resolved;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(item) {
  return item.value && item.value !== NOT_AVAILABLE;
}
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"], internalReferral = false): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return internalReferral ? "Selected because this is an internal Biltmore-to-Biltmore referral, so the provider named on the referral takes precedence over appointment history." : "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(37);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    diagnosisCategories,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp3);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== diagnosisCategories || $[9] !== evidence.internalReferral || $[10] !== evidence.pastAppointmentCount || $[11] !== evidence.selectedStep || $[12] !== evidence.upcomingAppointmentCount || $[13] !== providerCountSummary || $[14] !== referralProviderName || $[15] !== referralProviderNpi || $[16] !== selectedLocation || $[17] !== selectedProviderName) {
    const upcomingAppointmentStep = {
      key: "upcoming_appointment" as const,
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("upcoming")
      }]
    };
    const pastAppointmentStep = {
      key: "past_appointment" as const,
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("past")
      }]
    };
    const referralProviderStep = {
      key: "referral_provider" as const,
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    };
    const diagnosisMatchStep = {
      key: "diagnosis_match" as const,
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Diagnosis category",
        value: diagnosisCategories
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    };
    const steps = evidence.internalReferral ? [referralProviderStep, upcomingAppointmentStep, pastAppointmentStep, diagnosisMatchStep] : [upcomingAppointmentStep, pastAppointmentStep, referralProviderStep, diagnosisMatchStep];
    t6 = "space-y-2 border-t pt-3";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-foreground/40 bg-muted p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-foreground/40 bg-muted font-medium text-foreground">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep, evidence.internalReferral)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = diagnosisCategories;
    $[9] = evidence.internalReferral;
    $[10] = evidence.pastAppointmentCount;
    $[11] = evidence.selectedStep;
    $[12] = evidence.upcomingAppointmentCount;
    $[13] = providerCountSummary;
    $[14] = referralProviderName;
    $[15] = referralProviderNpi;
    $[16] = selectedLocation;
    $[17] = selectedProviderName;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
  }
  let t8;
  if ($[24] !== t3 || $[25] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[27] = evidence.selectedStep;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t5 || $[30] !== t8 || $[31] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t6 || $[35] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[33] = t10;
    $[34] = t6;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp3(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "completed";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(27);
  const {
    task,
    step,
    failedStepKey,
    blockedBy,
    awaitingStep
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : awaitingStep ? "not_run" : step.status;
  const approvalMutation = useApproveTaskMutation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidateOnSettled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const localTaskUpdateMutation = useLocalTaskUpdateMutation(t1);
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");
  let t2;
  if ($[1] !== failedStepKey || $[2] !== step.continuation || $[3] !== task) {
    t2 = failedStepKey ? {
      instruction: `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed. Complete it manually in Athena, then continue with the remaining steps.`,
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} />
    } : step.continuation;
    $[1] = failedStepKey;
    $[2] = step.continuation;
    $[3] = task;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const continuation = t2;
  let t3;
  if ($[5] !== approvalMutation || $[6] !== invalidateInstructions || $[7] !== localTaskUpdateMutation || $[8] !== task) {
    t3 = async () => {
      await approvalMutation.mutateAsync({
        taskId: task.id,
        approved: true
      });
      localTaskUpdateMutation.mutate({
        ...task,
        status: TaskStatus.PROCESSING
      });
      refreshTprWorkflowProgress(invalidateInstructions);
    };
    $[5] = approvalMutation;
    $[6] = invalidateInstructions;
    $[7] = localTaskUpdateMutation;
    $[8] = task;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleVerificationComplete = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = blockedBy ? `Waiting on step ${blockedBy.number} (${blockedBy.title}), which failed.` : !continuation?.control && continuation?.instruction || step.detail;
  const t6 = step.continuation ? handleVerificationComplete : undefined;
  let t7;
  if ($[10] !== continuation) {
    t7 = continuation?.control ? <><p className="text-sm text-muted-foreground">{continuation.instruction}</p><div className="flex justify-center">{continuation.control}</div></> : undefined;
    $[10] = continuation;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== awaitingStep || $[13] !== step.content || $[14] !== step.preview) {
    t8 = awaitingStep ? <TimelineStepPreview items={step.preview} /> : step.content;
    $[12] = awaitingStep;
    $[13] = step.content;
    $[14] = step.preview;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== effectiveStatus || $[17] !== step.phase || $[18] !== step.stepKey || $[19] !== step.workflowStepKeys || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== task.id) {
    t9 = <TaskStepCard header={t4} status={effectiveStatus} phase={step.phase} detail={t5} taskId={task.id} stepKey={step.stepKey} workflowStepKeys={step.workflowStepKeys} onVerificationComplete={t6} action={t7}>{t8}</TaskStepCard>;
    $[16] = effectiveStatus;
    $[17] = step.phase;
    $[18] = step.stepKey;
    $[19] = step.workflowStepKeys;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = task.id;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Coverage rechecks and edits are available on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(103);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    athenaCoverageLive,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteToEnter,
    urgency,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    referralSourceOnFile,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  useTprLiveWorkflowRefresh(useInvalidateServerComponent("getReferralInstructions"));
  const {
    verdictByStepKey
  } = useTaskStepVerifications(task.id);
  let t1;
  if ($[0] !== verdictByStepKey) {
    t1 = verdictByStepKey.get(TPR_TASK_STEP_KEY.PROVIDER_LOCATION)?.verdict;
    $[0] = verdictByStepKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerAssignmentMarkedIncorrect = t1 === VerificationResult.INCORRECT;
  let t2;
  if ($[2] !== taskSteps) {
    t2 = taskSteps.some(_temp4);
    $[2] = taskSteps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isProviderFirst = t2;
  let t3;
  if ($[4] !== pastAppointments || $[5] !== resolvedReceiving.providerSelectionEvidence || $[6] !== upcomingAppointments) {
    t3 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      internalReferral: false,
      appointmentProviders: []
    };
    $[4] = pastAppointments;
    $[5] = resolvedReceiving.providerSelectionEvidence;
    $[6] = upcomingAppointments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const providerSelectionEvidence = t3;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let failedStepCard;
  let failedStepKey;
  let t4;
  let waitingForAssociatedReferral;
  if ($[8] !== actionNote || $[9] !== athenaCoverageLive.primary || $[10] !== athenaCoverageLive.secondary || $[11] !== bucketLabel || $[12] !== chartReferralAuths || $[13] !== chartRequestLetters || $[14] !== coverageReadback || $[15] !== decisionTrace || $[16] !== documentId || $[17] !== documentUploadLabels || $[18] !== followThroughEligible || $[19] !== hasMissingInformation || $[20] !== insuranceRequirements || $[21] !== insuranceStatusDisplay || $[22] !== internalNoteToEnter || $[23] !== isPrimaryCareProvider || $[24] !== isProviderFirst || $[25] !== opsCrossCheck.authorizationEndDate || $[26] !== opsCrossCheck.authorizationNumber || $[27] !== opsCrossCheck.authorizationServices || $[28] !== opsCrossCheck.authorizationStartDate || $[29] !== opsCrossCheck.authorizationStatus || $[30] !== opsCrossCheck.authorizationVisits || $[31] !== opsCrossCheck.diagnosis || $[32] !== opsCrossCheck.diagnosisCategories || $[33] !== opsCrossCheck.emergencyContactName || $[34] !== opsCrossCheck.emergencyContactPhoneNumber || $[35] !== opsCrossCheck.emergencyContactRelationship || $[36] !== opsCrossCheck.isSecondarySpecialCaseInsurance || $[37] !== opsCrossCheck.isSpecialCaseInsurance || $[38] !== opsCrossCheck.patientDateOfBirth || $[39] !== opsCrossCheck.patientEthnicity || $[40] !== opsCrossCheck.patientFaxNumber || $[41] !== opsCrossCheck.patientHomeAddress || $[42] !== opsCrossCheck.patientLanguage || $[43] !== opsCrossCheck.patientName || $[44] !== opsCrossCheck.patientPhoneNumber || $[45] !== opsCrossCheck.patientRace || $[46] !== opsCrossCheck.primaryPlanLabel || $[47] !== opsCrossCheck.primarySubscriberId || $[48] !== opsCrossCheck.receivingProviderName || $[49] !== opsCrossCheck.receivingProviderNpi || $[50] !== opsCrossCheck.referralDate || $[51] !== opsCrossCheck.referringLocationAddress || $[52] !== opsCrossCheck.referringProviderName || $[53] !== opsCrossCheck.referringProviderNpi || $[54] !== opsCrossCheck.referringProviderSpecialty || $[55] !== opsCrossCheck.secondaryPlanLabel || $[56] !== opsCrossCheck.secondarySubscriberId || $[57] !== patientDisplayId || $[58] !== patientEhrId || $[59] !== patientLabel || $[60] !== pendingApproval || $[61] !== primaryCareProviderAlreadyPresent || $[62] !== privacyNoticeOnFile || $[63] !== providerAssignmentMarkedIncorrect || $[64] !== providerSelectionEvidence || $[65] !== receivingAssignmentConfirmed || $[66] !== referralAuthorization || $[67] !== referralId || $[68] !== referralSourceOnFile || $[69] !== resolvedReceiving.advisories || $[70] !== resolvedReceiving.flags || $[71] !== resolvedReceiving.locationEhrId || $[72] !== resolvedReceiving.locationLabel || $[73] !== resolvedReceiving.providerLabel || $[74] !== resolvedReceiving.providerNpi || $[75] !== resolvedReceiving.providerWriteSafe || $[76] !== resolvedReceiving.routedToSscardio || $[77] !== resolvedReceiving.usualProviderWriteSafe || $[78] !== task || $[79] !== taskSteps || $[80] !== unresolvedUpcomingProviders || $[81] !== upcomingAppointmentProviderSummary || $[82] !== urgency.isUrgent || $[83] !== urgency.reason || $[84] !== urgency.schedulingPriority || $[85] !== verdictByStepKey || $[86] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "completed" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "completed" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "completed" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "verification_required" : assignmentDependentStatus;
    const assignmentNeedsReview = !assignmentWasManuallyConfirmed && (assignmentRequiresManualReview || usesRetiredUsualProviderRule);
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentSelectionIncomplete = !resolvedReceiving.providerLabel?.trim() || !resolvedReceiving.providerNpi?.trim() || !resolvedReceiving.locationLabel?.trim();
    const assignmentResolved = assignmentStatus === "completed" || assignmentStatus === "failed";
    const assignmentConfirmationStatus = assignmentResolved && assignmentSelectionIncomplete ? "failed" : assignmentWasManuallyConfirmed ? "completed" : assignmentReviewStatus === "verification_required" ? "verification_required" : assignmentStatus === "completed" ? gateStatus : assignmentStatus;
    const providerAssignmentEditable = providerAssignmentMarkedIncorrect || assignmentConfirmationStatus === "failed";
    const confirmedNonCamelbackAssignment = assignmentStatus === "completed" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    const coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    const opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    waitingForAssociatedReferral = taskSteps.some(_temp5);
    const coverageReadbackComplete = taskSteps.some(_temp6);
    const authRequestUploaded = taskSteps.some(_temp7);
    const notesComplete = taskSteps.some(_temp8);
    const missingInfoUploadComplete = taskSteps.some(_temp9);
    const followThroughStatus = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
    const resolveConsolidatedStatus = _temp10;
    const livePrimaryAthenaPlan = athenaCoverageLive.primary && getCoverageDisplayName({
      coverage: athenaCoverageLive.primary
    }) || coverageReadback?.primary.planName || NOT_AVAILABLE;
    const liveSecondaryAthenaPlan = athenaCoverageLive.secondary && getCoverageDisplayName({
      coverage: athenaCoverageLive.secondary
    }) || coverageReadback?.secondary?.planName || NOT_AVAILABLE;
    const athenaCoverageDrifted = Boolean(coverageReadback && athenaCoverageLive.primary?.ehrId && athenaCoverageLive.primary.ehrId !== coverageReadback.primary.insuranceId);
    const primaryCoverageInAthena = Boolean(athenaCoverageLive.primary?.ehrId ?? coverageReadback?.primary.insuranceId);
    const insuranceStatus = primaryCoverageInAthena ? "completed" : workflowStillProcessing ? "waiting" : "failed";
    const authorizationStatus = opsKind === "authorization_review" ? "verification_required" : opsKind === "manual_authorization" ? "review_needed" : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "review_needed" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    const referralAuthNeedsPendingUpload = tprReferralAuthNeedsPendingUpload(referralAuthorization);
    const referralAuthNeedsOutboundFax = Boolean(insuranceRequirements?.referralMustBeFromPcp) || referralAuthNeedsPendingUpload;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "review_needed" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "review_needed" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "review_needed" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const closeDocumentStatus = opsKind === "close_document" ? "review_needed" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    const workflowNote = task.files.find(_temp11)?.note;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const patientChartStatus = !patientEhrId || patientEhrId === NOT_AVAILABLE ? "review_needed" : "verification_required";
    const fullRegistrationReviewReasons = [privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null, ensurePatientStatus === "completed" && referralSourceOnFile === null ? "Set \"How did you hear about us\" to PHYSICIAN REFERRAL manually." : null].filter(_temp12);
    const demographicsReviewReasons = [workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null].filter(_temp13);
    const pcpDisposition = !isPrimaryCareProvider ? "No \u2014 not a primary care specialty" : primaryCareProviderAlreadyPresent ? "Already the patient's PCP in Athena" : "Yes \u2014 added as the patient's PCP";
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "failed" : privacyNoticeOnFile === null ? "verification_required" : "completed";
    const hasEmergencyContactToEnter = opsCrossCheck.emergencyContactName !== NOT_AVAILABLE || opsCrossCheck.emergencyContactPhoneNumber !== NOT_AVAILABLE;
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "completed" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "failed" : "completed";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? "Yes" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    const referralRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
    const authorizationRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
    const insuranceCrossCheckItems = [{
      label: "Valerie plan",
      value: opsCrossCheck.primaryPlanLabel
    }, {
      label: "Valerie subscriber ID",
      value: opsCrossCheck.primarySubscriberId
    }, {
      label: "In network",
      value: inNetworkDisplay
    }, {
      label: "Referral",
      value: referralRequirementDisplay
    }, {
      label: "Authorization",
      value: authorizationRequirementDisplay
    }];
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp14)}</div></div> : followThroughEligible || assignmentStatus === "completed" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const steps = [{
      number: 1,
      stepKey: TPR_TASK_STEP_KEY.LOCATE_DOCUMENT,
      title: "Patient Chart",
      status: patientChartStatus,
      summary: "Copy and search by the document ID in Referral.",
      preview: [{
        label: "Patient",
        value: opsCrossCheck.patientName
      }, {
        label: "EHR ID",
        value: patientEhrId
      }],
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient",
          value: <CopyableContent text={`${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`} textClassName="text-xs" />
        }, {
          label: "EHR ID",
          value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
        }, {
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }, {
          label: "Phone",
          value: <CopyableContent text={opsCrossCheck.patientPhoneNumber} textClassName="text-xs" />
        }, {
          label: "Fax",
          value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
        }, {
          label: "Ethnicity",
          value: opsCrossCheck.patientEthnicity
        }, {
          label: "Race",
          value: opsCrossCheck.patientRace
        }, {
          label: "Language",
          value: opsCrossCheck.patientLanguage
        }]} />{patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null}</div>
    }, {
      number: 2,
      stepKey: TPR_TASK_STEP_KEY.PROVIDER_LOCATION,
      title: "Receiving Provider and Location",
      status: assignmentConfirmationStatus,
      workflowStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      preview: [{
        label: "Patient type",
        value: patientLabel
      }, {
        label: "Provider",
        value: resolvedReceiving.providerLabel
      }, {
        label: "Location",
        value: resolvedReceiving.locationLabel
      }],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentNeedsReview ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient type",
          value: patientLabel
        }, {
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Location",
          value: <CopyableContent text={resolvedReceiving.locationLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Resolution",
          value: usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed"
        }]} />{providerAssignmentEditable ? <TprReceivingAssignmentEditor task={task} currentProviderNpi={resolvedReceiving.providerNpi || null} currentDepartmentId={resolvedReceiving.locationEhrId || null} /> : null}<ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} diagnosisCategories={opsCrossCheck.diagnosisCategories} selectedProviderName={resolvedReceiving.providerLabel || NOT_AVAILABLE} selectedLocation={resolvedReceiving.locationLabel || NOT_AVAILABLE} />{resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp15)}</ul></div> : null}{assignmentNeedsReview && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp16)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp17)}</div> : null}</> : null}</div>,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      stepKey: TPR_TASK_STEP_KEY.DEMOGRAPHICS_CARE_TEAM,
      title: "Demographics and Care Team",
      preview: [{
        label: "PCP",
        value: pcpDisposition
      }],
      status: demographicsStatus,
      phase: hasEmergencyContactToEnter ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: hasEmergencyContactToEnter ? "Automation can't add the emergency contact. Enter it in Athena, then mark this updated." : undefined,
      workflowStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: hasEmergencyContactToEnter ? "Add the emergency contact to Athena by hand, then review patient and care team details in Quickview." : "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Patient Details</p><DecisionMetadata items={[{
            label: "Patient",
            value: opsCrossCheck.patientName
          }, {
            label: "DOB",
            value: opsCrossCheck.patientDateOfBirth
          }, {
            label: "Phone",
            value: opsCrossCheck.patientPhoneNumber
          }, {
            label: "Home address",
            value: opsCrossCheck.patientHomeAddress
          }]} /></div><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Care Team</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p><DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
              label: "Provider",
              value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "Result",
              value: "Updated for new patient"
            }] : [{
              label: "Result",
              value: "Left unchanged for established patient"
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }, {
              label: "Specialty",
              value: opsCrossCheck.referringProviderSpecialty
            }, {
              label: "Added as PCP",
              value: pcpDisposition
            }]} /></div></div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Emergency Contact</p><DecisionMetadata items={[{
            label: "Name",
            value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
          }, {
            label: "Relationship",
            value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
          }, {
            label: "Phone",
            value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
          }]} /></div>{demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp18)}</ul> : null}</div>
    }, {
      number: 4,
      stepKey: TPR_TASK_STEP_KEY.INSURANCE,
      title: "Insurance",
      preview: [{
        label: "Plan",
        value: opsCrossCheck.primaryPlanLabel
      }, {
        label: "In network",
        value: insuranceRequirements ? inNetworkDisplay : null
      }],
      status: insuranceStatus,
      phase: coverageApproval ? TaskStepPhase.ACTION_NEEDED : undefined,
      workflowStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: <div className="space-y-3">{athenaCoverageDrifted ? <p className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-foreground">Athena's primary insurance has changed since this referral synced. The network and eligibility results below were decided against the coverage we wrote, not the one on the chart now — re-check them before relying on them.</p> : null}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Primary Insurance</p><DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />{opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={livePrimaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: primaryCoverageInAthena ? "Yes" : "No"
          }, {
            label: "In network",
            value: coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay
          }]} />{coverageReadback && insuranceRequirements ? <TprNetworkConfirmationButton task={task} steps={taskSteps} coverage={coverageReadback} requirements={insuranceRequirements} /> : null}</div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Secondary Insurance</p>{opsCrossCheck.secondaryPlanLabel || coverageReadback?.secondary || athenaCoverageLive.secondary ? <DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.secondaryPlanLabel || NOT_AVAILABLE} textClassName="text-xs" />{opsCrossCheck.isSecondarySpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.secondarySubscriberId || NOT_AVAILABLE} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={liveSecondaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: athenaCoverageLive.secondary || coverageReadback?.secondary ? "Yes" : "No"
          }]} /> : <p className="text-sm text-muted-foreground">No secondary insurance was captured from the referral or found in Athena.</p>}</div><details className="text-xs"><summary className="cursor-pointer text-muted-foreground">More insurance detail</summary><div className="mt-3 space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Coverage</p><DecisionMetadata items={[{
                label: "Self pay",
                value: coverageReadback?.primary.selfPay ? "Yes" : "No"
              }]} /><TprStediEligibilityValues referralId={referralId} />{insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null}</div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Athena Eligibility</p>{followThroughEligible ? <DecisionMetadata items={[{
                label: "Athena eligibility",
                value: insuranceStatusDisplay
              }]} /> : <p className="text-sm text-muted-foreground">{skippedFollowThroughSummary}</p>}</div>{insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp19)}</ul></div> : null}</div></details></div>,
      continuation: coverageApproval ? {
        instruction: "Choose each policy from Valerie or Athena, including its subscriber details, then sync to continue.",
        control: <AthenaCoverageResolutionDialog taskId={task.id} />
      } : undefined
    }, {
      number: 5,
      stepKey: TPR_TASK_STEP_KEY.REFERRAL_AUTHORIZATION,
      title: "Referral Authorization",
      status: resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]),
      phase: referralAuthNeedsOutboundFax ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: referralAuthNeedsOutboundFax ? "This authorization needs an outbound fax, which isn't automated. Send it from Athena, then continue." : undefined,
      workflowStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary,
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">PCP Referral Fax</p>{insuranceRequirements ? <DecisionMetadata items={[{
            label: "PCP referral",
            value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
          }, {
            label: "Referring is PCP",
            value: isPrimaryCareProvider ? "Yes" : "No"
          }]} /> : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}</div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization on the Referral</p><DecisionMetadata items={[{
            label: "Auth number",
            value: <CopyableContent text={opsCrossCheck.authorizationNumber} textClassName="text-xs" />
          }, {
            label: "Status",
            value: opsCrossCheck.authorizationStatus
          }, {
            label: "Start date",
            value: opsCrossCheck.authorizationStartDate
          }, {
            label: "End date",
            value: opsCrossCheck.authorizationEndDate
          }, {
            label: "Visits",
            value: opsCrossCheck.authorizationVisits
          }, ...(opsCrossCheck.authorizationServices ? [{
            label: "Services",
            value: opsCrossCheck.authorizationServices
          }] : [])]} /></div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Referral Authorization</p>{insuranceRequirements ? <DecisionMetadata items={[{
            label: "Authorization",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
          }, {
            label: "Referral",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
          }, {
            label: "Outcome",
            value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
          }, ...(referralAuthorization?.outcome === "created" ? [{
            label: "Athena object",
            value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
          }, {
            label: "Appointment",
            value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
          }] : [])]} /> : null}<div className="space-y-2"><p className="text-sm font-medium">On the Athena chart</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referral authorizations</p>{chartReferralAuths.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartReferralAuths.map(_temp20)}</ul> : <p className="text-sm text-muted-foreground">None on this insurance.</p>}</div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p>{chartRequestLetters.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartRequestLetters.map(_temp21)}</ul> : <p className="text-sm text-muted-foreground">None uploaded on this task.</p>}</div></div><TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)} taskId={task.id} />{pcpFaxStatus === "review_needed" || authorizationStatus === "review_needed" ? <details className="text-xs"><summary className="text-muted-foreground">How to complete this manually</summary><div className="mt-2 space-y-3"><div><p className="font-medium text-foreground">Referral Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li><li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div><div><p className="font-medium text-foreground">Referral Authorization Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li><li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details> : null}</div></div>,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      stepKey: TPR_TASK_STEP_KEY.OUTBOUND_FAXES,
      detail: "Sending these faxes is still partly manual. Send them from Athena, then continue.",
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      workflowStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: <div className="space-y-3">{referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
            label: "Request letter",
            value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
          }]} />{outboundFaxStatus === "review_needed" ? <ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Upload request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol> : null}</div> : null}{hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
            label: "Missing-info packet",
            value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
          }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null}{outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null}</div>,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, upload the request letter.",
        control: <TprOpsContinueButton task={task} label="Upload request letter" pendingLabel="Uploading..." successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      stepKey: TPR_TASK_STEP_KEY.FULL_REGISTRATION,
      title: "Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Calls",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Texts",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Privacy Notice",
          value: privacyNoticeOnFile === null ? "Not read back" : privacyNoticeOnFile ? "Selected" : "Not selected"
        }, {
          label: "How did you hear about us",
          value: "Set to Physician Referral only if blank \u2014 existing value is not overridden"
        }]} />{fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp22)}</ul> : null}</div>
    }, {
      number: 8,
      stepKey: TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT,
      detail: "Parts of the referral document have to be keyed into Athena by hand. Enter them, then mark this updated.",
      title: "Referral Document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      workflowStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }]} /><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Referral Fields</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p><DecisionMetadata items={[{
              label: "Patient type",
              value: <CopyableContent text={patientLabel} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Referral Date</p><DecisionMetadata items={[{
              label: "Referral date",
              value: <CopyableContent text={opsCrossCheck.referralDate} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p><DecisionMetadata items={[{
              label: "Urgent",
              value: urgency.isUrgent ? "Yes" : "No"
            }, ...(urgency.reason ? [{
              label: "Reason",
              value: urgency.reason
            }] : []), {
              label: "Scheduling priority",
              value: urgency.schedulingPriority || NOT_AVAILABLE
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p><DecisionMetadata items={[{
              label: "Internal Note",
              value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />
            }]} /></div></div></div>
    }, {
      number: 9,
      stepKey: TPR_TASK_STEP_KEY.DOCUMENT_UPLOAD,
      title: "Document Upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: documentUploadLabels.length ? "Confirm the expected documents were uploaded to Athena." : "No documents were selected for upload.",
      workflowStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Documents",
          value: documentUploadLabels.length
        }]} /><div className="space-y-1"><p className="text-sm font-medium">Expected Athena uploads</p>{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp23)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div></div>
    }, {
      number: 10,
      stepKey: TPR_TASK_STEP_KEY.QUICKVIEW,
      title: "Quickview Note",
      status: notesComplete ? "verification_required" : dispositionStatus,
      workflowStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified.",
      content: notesComplete ? <p className="text-sm">Confirm the final note appears correctly on the patient's Quickview tab in Athena.</p> : undefined
    }, {
      number: 11,
      stepKey: TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT,
      detail: "Choosing the bucket is ops' call. Assign it in Athena, then mark this updated.",
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "review_needed" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      workflowStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Bucket</p><DecisionMetadata items={[{
            label: "Suggested bucket",
            value: <CopyableContent text={bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} />
          }, {
            label: "Missing information",
            value: hasMissingInformation ? "Required" : "No"
          }, {
            label: "Authorization request",
            value: authRequestUploaded ? "Uploaded" : "Not required"
          }]} />{decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp24)}</ul></details> : null}</div>{!resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Action Notes</p><DecisionMetadata items={[{
            label: "Action Notes",
            value: <CopyableContent text={actionNote} textClassName="text-xs" />
          }]} /></div></div>,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : undefined
    }];
    const failedWorkflowStepKeys = new Set(taskSteps.filter(_temp25).map(_temp26));
    let t5;
    if ($[91] !== taskSteps) {
      t5 = taskSteps.find(_temp27);
      $[91] = taskSteps;
      $[92] = t5;
    } else {
      t5 = $[92];
    }
    const failedTaskStep = t5;
    failedStepCard = failedTaskStep ? steps.find(step_8 => step_8.workflowStepKeys.some(stepKey => stepKey === failedTaskStep.stepKey)) : undefined;
    failedStepKey = failedTaskStep && failedStepCard ? failedStepCard.workflowStepKeys.find(stepKey_0 => stepKey_0 === failedTaskStep.stepKey) ?? null : null;
    let sequenceOpen = true;
    let sequenceBlockedBy = null;
    t4 = steps.map(step_9 => {
      const pinnedPhase = step_9.phase ?? TPR_PINNED_STEP_PHASE[step_9.stepKey];
      const isFailedSync = step_9.number === failedStepCard?.number;
      const naturalPhase = resolveTaskStepPhase(isFailedSync ? "failed" : step_9.status, pinnedPhase);
      const verdict = verdictByStepKey.get(step_9.stepKey)?.verdict;
      const coversFailedStep = step_9.workflowStepKeys.some(stepKey_1 => failedWorkflowStepKeys.has(stepKey_1));
      const confirmed = !coversFailedStep && verdict === VerificationResult.CORRECT;
      const presented = sequenceOpen || isFailedSync;
      const phase = presented ? naturalPhase : null;
      const awaitingStep = presented ? null : sequenceBlockedBy;
      if (sequenceOpen && step_9.status !== "not_needed" && !(naturalPhase !== null && confirmed)) {
        sequenceOpen = false;
        sequenceBlockedBy = step_9;
      }
      return {
        step: {
          ...step_9,
          phase: pinnedPhase
        },
        phase,
        awaitingStep,
        naturalPhase
      };
    });
    $[8] = actionNote;
    $[9] = athenaCoverageLive.primary;
    $[10] = athenaCoverageLive.secondary;
    $[11] = bucketLabel;
    $[12] = chartReferralAuths;
    $[13] = chartRequestLetters;
    $[14] = coverageReadback;
    $[15] = decisionTrace;
    $[16] = documentId;
    $[17] = documentUploadLabels;
    $[18] = followThroughEligible;
    $[19] = hasMissingInformation;
    $[20] = insuranceRequirements;
    $[21] = insuranceStatusDisplay;
    $[22] = internalNoteToEnter;
    $[23] = isPrimaryCareProvider;
    $[24] = isProviderFirst;
    $[25] = opsCrossCheck.authorizationEndDate;
    $[26] = opsCrossCheck.authorizationNumber;
    $[27] = opsCrossCheck.authorizationServices;
    $[28] = opsCrossCheck.authorizationStartDate;
    $[29] = opsCrossCheck.authorizationStatus;
    $[30] = opsCrossCheck.authorizationVisits;
    $[31] = opsCrossCheck.diagnosis;
    $[32] = opsCrossCheck.diagnosisCategories;
    $[33] = opsCrossCheck.emergencyContactName;
    $[34] = opsCrossCheck.emergencyContactPhoneNumber;
    $[35] = opsCrossCheck.emergencyContactRelationship;
    $[36] = opsCrossCheck.isSecondarySpecialCaseInsurance;
    $[37] = opsCrossCheck.isSpecialCaseInsurance;
    $[38] = opsCrossCheck.patientDateOfBirth;
    $[39] = opsCrossCheck.patientEthnicity;
    $[40] = opsCrossCheck.patientFaxNumber;
    $[41] = opsCrossCheck.patientHomeAddress;
    $[42] = opsCrossCheck.patientLanguage;
    $[43] = opsCrossCheck.patientName;
    $[44] = opsCrossCheck.patientPhoneNumber;
    $[45] = opsCrossCheck.patientRace;
    $[46] = opsCrossCheck.primaryPlanLabel;
    $[47] = opsCrossCheck.primarySubscriberId;
    $[48] = opsCrossCheck.receivingProviderName;
    $[49] = opsCrossCheck.receivingProviderNpi;
    $[50] = opsCrossCheck.referralDate;
    $[51] = opsCrossCheck.referringLocationAddress;
    $[52] = opsCrossCheck.referringProviderName;
    $[53] = opsCrossCheck.referringProviderNpi;
    $[54] = opsCrossCheck.referringProviderSpecialty;
    $[55] = opsCrossCheck.secondaryPlanLabel;
    $[56] = opsCrossCheck.secondarySubscriberId;
    $[57] = patientDisplayId;
    $[58] = patientEhrId;
    $[59] = patientLabel;
    $[60] = pendingApproval;
    $[61] = primaryCareProviderAlreadyPresent;
    $[62] = privacyNoticeOnFile;
    $[63] = providerAssignmentMarkedIncorrect;
    $[64] = providerSelectionEvidence;
    $[65] = receivingAssignmentConfirmed;
    $[66] = referralAuthorization;
    $[67] = referralId;
    $[68] = referralSourceOnFile;
    $[69] = resolvedReceiving.advisories;
    $[70] = resolvedReceiving.flags;
    $[71] = resolvedReceiving.locationEhrId;
    $[72] = resolvedReceiving.locationLabel;
    $[73] = resolvedReceiving.providerLabel;
    $[74] = resolvedReceiving.providerNpi;
    $[75] = resolvedReceiving.providerWriteSafe;
    $[76] = resolvedReceiving.routedToSscardio;
    $[77] = resolvedReceiving.usualProviderWriteSafe;
    $[78] = task;
    $[79] = taskSteps;
    $[80] = unresolvedUpcomingProviders;
    $[81] = upcomingAppointmentProviderSummary;
    $[82] = urgency.isUrgent;
    $[83] = urgency.reason;
    $[84] = urgency.schedulingPriority;
    $[85] = verdictByStepKey;
    $[86] = workflowStillProcessing;
    $[87] = failedStepCard;
    $[88] = failedStepKey;
    $[89] = t4;
    $[90] = waitingForAssociatedReferral;
  } else {
    failedStepCard = $[87];
    failedStepKey = $[88];
    t4 = $[89];
    waitingForAssociatedReferral = $[90];
  }
  const phasedSteps = t4;
  const timelineNotice = waitingForAssociatedReferral ? "Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing." : phasedSteps.some(_temp28) ? null : workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain.";
  let t5;
  if ($[93] !== timelineNotice) {
    t5 = timelineNotice ? <p className="text-sm text-muted-foreground">{timelineNotice}</p> : null;
    $[93] = timelineNotice;
    $[94] = t5;
  } else {
    t5 = $[94];
  }
  let t6;
  if ($[95] !== failedStepCard || $[96] !== failedStepKey || $[97] !== phasedSteps || $[98] !== task) {
    t6 = phasedSteps.map(t7 => {
      const {
        step: step_10,
        naturalPhase: naturalPhase_0,
        awaitingStep: awaitingStep_0
      } = t7;
      return <TimelineStepCard key={step_10.number} task={task} step={step_10} failedStepKey={step_10.number === failedStepCard?.number ? failedStepKey : null} blockedBy={naturalPhase_0 === null && failedStepCard && failedStepCard.number !== step_10.number ? failedStepCard : null} awaitingStep={awaitingStep_0} />;
    });
    $[95] = failedStepCard;
    $[96] = failedStepKey;
    $[97] = phasedSteps;
    $[98] = task;
    $[99] = t6;
  } else {
    t6 = $[99];
  }
  let t7;
  if ($[100] !== t5 || $[101] !== t6) {
    t7 = <div className="space-y-8 pb-8">{t5}{t6}</div>;
    $[100] = t5;
    $[101] = t6;
    $[102] = t7;
  } else {
    t7 = $[102];
  }
  return t7;
}
function _temp28(entry_0) {
  return entry_0.phase !== null;
}
function _temp27(step_7) {
  return step_7.status === TaskStepStatus.FAILED;
}
function _temp26(step_6) {
  return step_6.stepKey;
}
function _temp25(step_5) {
  return step_5.status === TaskStepStatus.FAILED;
}
function _temp24(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp23(label) {
  return <li key={label}>{label}</li>;
}
function _temp22(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp21(letter) {
  return <li key={letter.documentEhrId}><CopyableContent text={letter.documentEhrId} textClassName="text-xs" />{letter.documentDate ? ` · ${letter.documentDate}` : ""}{letter.description ? ` · ${letter.description}` : ""}</li>;
}
function _temp20(authorization) {
  return <li key={authorization.referralAuthId}><CopyableContent text={authorization.referralAuthId} textClassName="text-xs" />{authorization.number ? ` · ${authorization.number}` : ""}{authorization.startDate || authorization.endDate ? ` · ${authorization.startDate ?? "\u2014"}–${authorization.endDate ?? "\u2014"}` : ""}{authorization.expired ? " \xB7 expired" : ""}</li>;
}
function _temp19(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp18(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp17(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp16(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp15(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp14(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp13(reason_0) {
  return reason_0 !== null;
}
function _temp12(reason) {
  return reason !== null;
}
function _temp11(file) {
  return file.type === FileType.INBOUND;
}
function _temp10(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("review_needed")) {
    return "review_needed";
  }
  if (statuses.includes("verification_required")) {
    return "verification_required";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("completed")) {
    return "completed";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp9(step_4) {
  return step_4.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp8(step_3) {
  return step_3.stepKey === StepKey.REFERRAL_DISPOSITION && step_3.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_2) {
  return step_2.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_2.status === TaskStepStatus.COMPLETED && step_2.output?.uploaded === true;
}
function _temp6(step_1) {
  return step_1.stepKey === StepKey.READ_ATHENA_COVERAGE && step_1.status === TaskStepStatus.COMPLETED;
}
function _temp5(step_0) {
  return step_0.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_0.status === TaskStepStatus.PAUSED;
}
function _temp4(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 8 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 8 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  /** Athena's current `referralsourceid`, or null when blank. */
  referralSourceOnFile: string | null | undefined;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    /** Athena department id behind `locationLabel` — preselects the ops location picker. */
    locationEhrId: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
}
export function TprReferralInstructions(t0) {
  const $ = _c(450);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    urgency,
    expectedInternalNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    referralSourceOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.dateOfReferral) {
    t6 = formatDateMmDdYyyy(referral.dateOfReferral) || NOT_AVAILABLE;
    $[10] = referral.dateOfReferral;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const dateOfReferral = t6;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER) ? athenaInternalNote : expectedInternalNote;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t7;
  let t8;
  let t9;
  if ($[12] !== actionNote || $[13] !== athenaData?.coverages || $[14] !== athenaData?.patientDetails || $[15] !== bucketLabel || $[16] !== chartReferralAuths || $[17] !== chartRequestLetters || $[18] !== coverageReadback || $[19] !== dateOfReferral || $[20] !== decisionTrace || $[21] !== documentId || $[22] !== documentUploadLabels || $[23] !== eligibilityDisplay || $[24] !== emergencyContactName || $[25] !== emergencyContactPhoneNumber || $[26] !== emergencyContactRelationship || $[27] !== followThroughEligible || $[28] !== insuranceRequirements || $[29] !== internalNoteToEnter || $[30] !== isPrimaryCareProvider || $[31] !== pastAppointments || $[32] !== patient || $[33] !== patientEhrId || $[34] !== patientLabel || $[35] !== pendingApproval || $[36] !== primaryCareProviderAlreadyPresent || $[37] !== primaryCoverageSummary || $[38] !== privacyNoticeOnFile || $[39] !== receivingAssignmentConfirmed || $[40] !== receivingProviderName || $[41] !== referral || $[42] !== referralAuthorization || $[43] !== referralSourceOnFile || $[44] !== referringLocationAddress || $[45] !== referringProviderName || $[46] !== referringProviderSpecialty || $[47] !== resolvedReceiving || $[48] !== secondaryCoverageSummary || $[49] !== task || $[50] !== taskSteps || $[51] !== unresolvedUpcomingProviders || $[52] !== upcomingAppointmentProviderSummary || $[53] !== upcomingAppointments || $[54] !== urgency || $[55] !== view) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t26;
      if ($[79] !== patient) {
        t26 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[79] = patient;
        $[80] = t26;
      } else {
        t26 = $[80];
      }
      const referralPhone = t26;
      let t27;
      if ($[81] !== chartPatient) {
        t27 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[81] = chartPatient;
        $[82] = t27;
      } else {
        t27 = $[82];
      }
      const chartPhone = t27;
      let chartAddressValue;
      let phoneDataSourceEntry;
      let referralAddressValue;
      let t28;
      let t29;
      let t30;
      if ($[83] !== athenaData?.coverages || $[84] !== chartPatient || $[85] !== chartPhone || $[86] !== patient || $[87] !== referral.primaryCoverage || $[88] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp29)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        let t31;
        if ($[95] !== chartPhone || $[96] !== referralPhone) {
          t31 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[95] = chartPhone;
          $[96] = referralPhone;
          $[97] = t31;
        } else {
          t31 = $[97];
        }
        phoneDataSourceEntry = t31;
        t28 = phoneDataSourceEntry;
        t29 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t30 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[83] = athenaData?.coverages;
        $[84] = chartPatient;
        $[85] = chartPhone;
        $[86] = patient;
        $[87] = referral.primaryCoverage;
        $[88] = referralPhone;
        $[89] = chartAddressValue;
        $[90] = phoneDataSourceEntry;
        $[91] = referralAddressValue;
        $[92] = t28;
        $[93] = t29;
        $[94] = t30;
      } else {
        chartAddressValue = $[89];
        phoneDataSourceEntry = $[90];
        referralAddressValue = $[91];
        t28 = $[92];
        t29 = $[93];
        t30 = $[94];
      }
      let t31;
      if ($[98] !== t28 || $[99] !== t29 || $[100] !== t30) {
        t31 = [t28, t29, t30];
        $[98] = t28;
        $[99] = t29;
        $[100] = t30;
        $[101] = t31;
      } else {
        t31 = $[101];
      }
      dataSourceEntries = t31;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t32;
      if ($[102] !== isCamelbackReferral || $[103] !== phoneDataSourceEntry) {
        t32 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[102] = isCamelbackReferral;
        $[103] = phoneDataSourceEntry;
        $[104] = t32;
      } else {
        t32 = $[104];
      }
      missingFields = t32;
      hasMissingInformation = missingFields.length > 0;
      let t33;
      if ($[105] !== patient) {
        t33 = getPatientDisplayName(patient);
        $[105] = patient;
        $[106] = t33;
      } else {
        t33 = $[106];
      }
      const patientDisplayName = t33;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t34;
      if ($[107] !== referral.diagnosisList || $[108] !== referral.reasonForReferral) {
        t34 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp30)].filter(_temp31).filter(_temp32).join(", ") || NOT_AVAILABLE;
        $[107] = referral.diagnosisList;
        $[108] = referral.reasonForReferral;
        $[109] = t34;
      } else {
        t34 = $[109];
      }
      const diagnosis = t34;
      let t35;
      if ($[110] !== referral) {
        t35 = describeTprSpecialtyCategories(matchTprDiagnosisSpecialties(referral)) || "None matched";
        $[110] = referral;
        $[111] = t35;
      } else {
        t35 = $[111];
      }
      const diagnosisCategories = t35;
      let t36;
      if ($[112] !== patient) {
        t36 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[112] = patient;
        $[113] = t36;
      } else {
        t36 = $[113];
      }
      let t37;
      if ($[114] !== patient?.dateOfBirth) {
        t37 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[114] = patient?.dateOfBirth;
        $[115] = t37;
      } else {
        t37 = $[115];
      }
      const t38 = chartPhone ?? referralPhone ?? NOT_AVAILABLE;
      let t39;
      if ($[116] !== patient) {
        t39 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[116] = patient;
        $[117] = t39;
      } else {
        t39 = $[117];
      }
      const t40 = patient?.ethnicity || NOT_AVAILABLE;
      const t41 = patient?.race || NOT_AVAILABLE;
      const t42 = patient?.language || NOT_AVAILABLE;
      const t43 = chartAddressValue ?? referralAddressValue ?? NOT_AVAILABLE;
      const t44 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t45 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t46 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t47;
      if ($[118] !== referral.primaryCoverage) {
        t47 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[118] = referral.primaryCoverage;
        $[119] = t47;
      } else {
        t47 = $[119];
      }
      const t48 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      const t49 = referral.primaryCoverage?.payer;
      let t50;
      if ($[120] !== t49) {
        t50 = isTprSpecialCaseInsurance(t49);
        $[120] = t49;
        $[121] = t50;
      } else {
        t50 = $[121];
      }
      let t51;
      if ($[122] !== referral.secondaryCoverage) {
        t51 = referral.secondaryCoverage ? getCoverageDisplayName({
          coverage: referral.secondaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : null;
        $[122] = referral.secondaryCoverage;
        $[123] = t51;
      } else {
        t51 = $[123];
      }
      const t52 = referral.secondaryCoverage?.subscriberId || null;
      const t53 = referral.secondaryCoverage?.payer;
      let t54;
      if ($[124] !== t53) {
        t54 = isTprSpecialCaseInsurance(t53);
        $[124] = t53;
        $[125] = t54;
      } else {
        t54 = $[125];
      }
      let t55;
      if ($[126] !== referral.authorizationNumber) {
        t55 = referral.authorizationNumber?.trim() || NOT_AVAILABLE;
        $[126] = referral.authorizationNumber;
        $[127] = t55;
      } else {
        t55 = $[127];
      }
      let t56;
      if ($[128] !== referral.authorizationStatus) {
        t56 = referral.authorizationStatus ? formatFromUnderscoreToHumanReadable(referral.authorizationStatus) : NOT_AVAILABLE;
        $[128] = referral.authorizationStatus;
        $[129] = t56;
      } else {
        t56 = $[129];
      }
      let t57;
      if ($[130] !== referral.startDate) {
        t57 = formatDateMmDdYyyy(referral.startDate) || NOT_AVAILABLE;
        $[130] = referral.startDate;
        $[131] = t57;
      } else {
        t57 = $[131];
      }
      let t58;
      if ($[132] !== referral.endDate) {
        t58 = formatDateMmDdYyyy(referral.endDate) || NOT_AVAILABLE;
        $[132] = referral.endDate;
        $[133] = t58;
      } else {
        t58 = $[133];
      }
      let t59;
      if ($[134] !== referral.numberOfVisits) {
        t59 = referral.numberOfVisits?.toString() ?? NOT_AVAILABLE;
        $[134] = referral.numberOfVisits;
        $[135] = t59;
      } else {
        t59 = $[135];
      }
      let t60;
      if ($[136] !== referral.numberOfServices) {
        t60 = referral.numberOfServices?.toString() ?? null;
        $[136] = referral.numberOfServices;
        $[137] = t60;
      } else {
        t60 = $[137];
      }
      let t61;
      if ($[138] !== dateOfReferral || $[139] !== diagnosis || $[140] !== diagnosisCategories || $[141] !== emergencyContactName || $[142] !== emergencyContactPhoneNumber || $[143] !== emergencyContactRelationship || $[144] !== receivingProviderName || $[145] !== referringLocationAddress || $[146] !== referringProviderName || $[147] !== referringProviderSpecialty || $[148] !== t36 || $[149] !== t37 || $[150] !== t38 || $[151] !== t39 || $[152] !== t40 || $[153] !== t41 || $[154] !== t42 || $[155] !== t43 || $[156] !== t44 || $[157] !== t45 || $[158] !== t46 || $[159] !== t47 || $[160] !== t48 || $[161] !== t50 || $[162] !== t51 || $[163] !== t52 || $[164] !== t54 || $[165] !== t55 || $[166] !== t56 || $[167] !== t57 || $[168] !== t58 || $[169] !== t59 || $[170] !== t60) {
        t61 = {
          patientName: t36,
          patientDateOfBirth: t37,
          patientPhoneNumber: t38,
          patientFaxNumber: t39,
          patientEthnicity: t40,
          patientRace: t41,
          patientLanguage: t42,
          patientHomeAddress: t43,
          receivingProviderName,
          receivingProviderNpi: t44,
          referringProviderName,
          referringProviderNpi: t45,
          referringProviderSpecialty,
          referringLocationName: t46,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          referralDate: dateOfReferral,
          primaryPlanLabel: t47,
          primarySubscriberId: t48,
          isSpecialCaseInsurance: t50,
          secondaryPlanLabel: t51,
          secondarySubscriberId: t52,
          isSecondarySpecialCaseInsurance: t54,
          authorizationNumber: t55,
          authorizationStatus: t56,
          authorizationStartDate: t57,
          authorizationEndDate: t58,
          authorizationVisits: t59,
          authorizationServices: t60,
          diagnosis,
          diagnosisCategories
        };
        $[138] = dateOfReferral;
        $[139] = diagnosis;
        $[140] = diagnosisCategories;
        $[141] = emergencyContactName;
        $[142] = emergencyContactPhoneNumber;
        $[143] = emergencyContactRelationship;
        $[144] = receivingProviderName;
        $[145] = referringLocationAddress;
        $[146] = referringProviderName;
        $[147] = referringProviderSpecialty;
        $[148] = t36;
        $[149] = t37;
        $[150] = t38;
        $[151] = t39;
        $[152] = t40;
        $[153] = t41;
        $[154] = t42;
        $[155] = t43;
        $[156] = t44;
        $[157] = t45;
        $[158] = t46;
        $[159] = t47;
        $[160] = t48;
        $[161] = t50;
        $[162] = t51;
        $[163] = t52;
        $[164] = t54;
        $[165] = t55;
        $[166] = t56;
        $[167] = t57;
        $[168] = t58;
        $[169] = t59;
        $[170] = t60;
        $[171] = t61;
      } else {
        t61 = $[171];
      }
      const opsCrossCheck = t61;
      let t62;
      let t63;
      if ($[172] !== athenaData?.coverages) {
        const sortedAthenaCoverages = [...(athenaData?.coverages ?? [])].sort(_temp33);
        t62 = sortedAthenaCoverages.find(_temp34) ?? sortedAthenaCoverages[0] ?? null;
        t63 = sortedAthenaCoverages.find(_temp35) ?? sortedAthenaCoverages[1] ?? null;
        $[172] = athenaData?.coverages;
        $[173] = t62;
        $[174] = t63;
      } else {
        t62 = $[173];
        t63 = $[174];
      }
      let t64;
      if ($[175] !== t62 || $[176] !== t63) {
        t64 = {
          primary: t62,
          secondary: t63
        };
        $[175] = t62;
        $[176] = t63;
        $[177] = t64;
      } else {
        t64 = $[177];
      }
      const athenaCoverageLive = t64;
      if (view === "steps") {
        let t65;
        if ($[178] !== actionNote || $[179] !== athenaCoverageLive || $[180] !== bucketLabel || $[181] !== chartReferralAuths || $[182] !== chartRequestLetters || $[183] !== coverageReadback || $[184] !== decisionTrace || $[185] !== documentId || $[186] !== documentUploadLabels || $[187] !== followThroughEligible || $[188] !== hasMissingInformation || $[189] !== insuranceRequirements || $[190] !== insuranceStatusDisplay || $[191] !== internalNoteToEnter || $[192] !== isPrimaryCareProvider || $[193] !== opsCrossCheck || $[194] !== pastAppointments || $[195] !== patient.displayId || $[196] !== patientEhrId || $[197] !== patientLabel || $[198] !== pendingApproval || $[199] !== primaryCareProviderAlreadyPresent || $[200] !== privacyNoticeOnFile || $[201] !== receivingAssignmentConfirmed || $[202] !== referral.id || $[203] !== referralAuthorization || $[204] !== referralSourceOnFile || $[205] !== resolvedReceiving || $[206] !== task || $[207] !== taskSteps || $[208] !== unresolvedUpcomingProviders || $[209] !== upcomingAppointmentProviderSummary || $[210] !== upcomingAppointments || $[211] !== urgency) {
          t65 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} athenaCoverageLive={athenaCoverageLive} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteToEnter={internalNoteToEnter} urgency={urgency} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} referralSourceOnFile={referralSourceOnFile} isPrimaryCareProvider={isPrimaryCareProvider} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} chartReferralAuths={chartReferralAuths} chartRequestLetters={chartRequestLetters} /></div>;
          $[178] = actionNote;
          $[179] = athenaCoverageLive;
          $[180] = bucketLabel;
          $[181] = chartReferralAuths;
          $[182] = chartRequestLetters;
          $[183] = coverageReadback;
          $[184] = decisionTrace;
          $[185] = documentId;
          $[186] = documentUploadLabels;
          $[187] = followThroughEligible;
          $[188] = hasMissingInformation;
          $[189] = insuranceRequirements;
          $[190] = insuranceStatusDisplay;
          $[191] = internalNoteToEnter;
          $[192] = isPrimaryCareProvider;
          $[193] = opsCrossCheck;
          $[194] = pastAppointments;
          $[195] = patient.displayId;
          $[196] = patientEhrId;
          $[197] = patientLabel;
          $[198] = pendingApproval;
          $[199] = primaryCareProviderAlreadyPresent;
          $[200] = privacyNoticeOnFile;
          $[201] = receivingAssignmentConfirmed;
          $[202] = referral.id;
          $[203] = referralAuthorization;
          $[204] = referralSourceOnFile;
          $[205] = resolvedReceiving;
          $[206] = task;
          $[207] = taskSteps;
          $[208] = unresolvedUpcomingProviders;
          $[209] = upcomingAppointmentProviderSummary;
          $[210] = upcomingAppointments;
          $[211] = urgency;
          $[212] = t65;
        } else {
          t65 = $[212];
        }
        t25 = t65;
        break bb0;
      }
      t24 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t23 = "Detailed Athena verification reference";
      t16 = "text-sm text-muted-foreground space-y-6";
      if ($[213] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[213] = t17;
      } else {
        t17 = $[213];
      }
      const t65 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[214] !== t65) {
        t18 = <p className="text-xs text-center">{t65}</p>;
        $[214] = t65;
        $[215] = t18;
      } else {
        t18 = $[215];
      }
      if ($[216] !== task.id) {
        t19 = <SyncIncompleteActions taskId={task.id} />;
        $[216] = task.id;
        $[217] = t19;
      } else {
        t19 = $[217];
      }
      let t66;
      if ($[218] === Symbol.for("react.memo_cache_sentinel")) {
        t66 = <strong>Camelback Referral:</strong>;
        $[218] = t66;
      } else {
        t66 = $[218];
      }
      const t67 = isCamelbackReferral ? "Yes" : "No";
      let t68;
      if ($[219] !== t67) {
        t68 = <p>{t66} {t67}</p>;
        $[219] = t67;
        $[220] = t68;
      } else {
        t68 = $[220];
      }
      let t69;
      if ($[221] === Symbol.for("react.memo_cache_sentinel")) {
        t69 = <strong>Patient:</strong>;
        $[221] = t69;
      } else {
        t69 = $[221];
      }
      let t70;
      if ($[222] !== patientDisplayName) {
        t70 = <CopyableContent text={patientDisplayName} />;
        $[222] = patientDisplayName;
        $[223] = t70;
      } else {
        t70 = $[223];
      }
      let t71;
      if ($[224] !== patientLabel || $[225] !== t70) {
        t71 = <p>{t69} {t70} ({patientLabel})</p>;
        $[224] = patientLabel;
        $[225] = t70;
        $[226] = t71;
      } else {
        t71 = $[226];
      }
      let t72;
      if ($[227] === Symbol.for("react.memo_cache_sentinel")) {
        t72 = <strong>Receiving Provider (as extracted):</strong>;
        $[227] = t72;
      } else {
        t72 = $[227];
      }
      let t73;
      if ($[228] !== referral.receivingProvider) {
        t73 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[228] = referral.receivingProvider;
        $[229] = t73;
      } else {
        t73 = $[229];
      }
      let t74;
      if ($[230] === Symbol.for("react.memo_cache_sentinel")) {
        t74 = <strong>Location:</strong>;
        $[230] = t74;
      } else {
        t74 = $[230];
      }
      let t75;
      if ($[231] !== receivingLocationName) {
        t75 = <CopyableContent text={receivingLocationName} />;
        $[231] = receivingLocationName;
        $[232] = t75;
      } else {
        t75 = $[232];
      }
      let t76;
      if ($[233] !== t73 || $[234] !== t75) {
        t76 = <p>{t72}{" "}{t73}, {t74}{" "}{t75}</p>;
        $[233] = t73;
        $[234] = t75;
        $[235] = t76;
      } else {
        t76 = $[235];
      }
      if ($[236] !== t68 || $[237] !== t71 || $[238] !== t76) {
        t20 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t68}{t71}{t76}</div>;
        $[236] = t68;
        $[237] = t71;
        $[238] = t76;
        $[239] = t20;
      } else {
        t20 = $[239];
      }
      let t77;
      let t78;
      if ($[240] === Symbol.for("react.memo_cache_sentinel")) {
        t77 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t78 = <strong>Provider:</strong>;
        $[240] = t77;
        $[241] = t78;
      } else {
        t77 = $[240];
        t78 = $[241];
      }
      const t79 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t80;
      if ($[242] !== t79) {
        t80 = <CopyableContent text={t79} />;
        $[242] = t79;
        $[243] = t80;
      } else {
        t80 = $[243];
      }
      let t81;
      if ($[244] === Symbol.for("react.memo_cache_sentinel")) {
        t81 = <strong>Location:</strong>;
        $[244] = t81;
      } else {
        t81 = $[244];
      }
      const t82 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t83;
      if ($[245] !== t82) {
        t83 = <CopyableContent text={t82} />;
        $[245] = t82;
        $[246] = t83;
      } else {
        t83 = $[246];
      }
      let t84;
      if ($[247] !== t80 || $[248] !== t83) {
        t84 = <p>{t78} {t80},{" "}{t81} {t83}</p>;
        $[247] = t80;
        $[248] = t83;
        $[249] = t84;
      } else {
        t84 = $[249];
      }
      let t85;
      if ($[250] !== resolvedReceiving.routedToSscardio) {
        t85 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[250] = resolvedReceiving.routedToSscardio;
        $[251] = t85;
      } else {
        t85 = $[251];
      }
      let t86;
      if ($[252] !== resolvedReceiving.flags) {
        t86 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp36)}</ul>;
        $[252] = resolvedReceiving.flags;
        $[253] = t86;
      } else {
        t86 = $[253];
      }
      let t87;
      if ($[254] !== resolvedReceiving.trace) {
        t87 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp37)}</ul></details>;
        $[254] = resolvedReceiving.trace;
        $[255] = t87;
      } else {
        t87 = $[255];
      }
      if ($[256] !== t84 || $[257] !== t85 || $[258] !== t86 || $[259] !== t87) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t77}{t84}{t85}{t86}{t87}</div>;
        $[256] = t84;
        $[257] = t85;
        $[258] = t86;
        $[259] = t87;
        $[260] = t21;
      } else {
        t21 = $[260];
      }
      let t88;
      if ($[261] === Symbol.for("react.memo_cache_sentinel")) {
        t88 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[261] = t88;
      } else {
        t88 = $[261];
      }
      let t89;
      if ($[262] !== documentId) {
        t89 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[262] = documentId;
        $[263] = t89;
      } else {
        t89 = $[263];
      }
      let t90;
      if ($[264] !== patientEhrId) {
        t90 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[264] = patientEhrId;
        $[265] = t90;
      } else {
        t90 = $[265];
      }
      let t91;
      if ($[266] === Symbol.for("react.memo_cache_sentinel")) {
        t91 = <li>Hit save and proceed to quickview.</li>;
        $[266] = t91;
      } else {
        t91 = $[266];
      }
      let t92;
      if ($[267] !== resolvedReceiving.providerLabel || $[268] !== resolvedReceiving.providerNpi || $[269] !== resolvedReceiving.usualProviderWriteSafe) {
        t92 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[267] = resolvedReceiving.providerLabel;
        $[268] = resolvedReceiving.providerNpi;
        $[269] = resolvedReceiving.usualProviderWriteSafe;
        $[270] = t92;
      } else {
        t92 = $[270];
      }
      const t93 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t94;
      if ($[271] !== t93) {
        t94 = <p className="text-xs italic mt-1">{t93}</p>;
        $[271] = t93;
        $[272] = t94;
      } else {
        t94 = $[272];
      }
      let t95;
      if ($[273] !== t92 || $[274] !== t94) {
        t95 = <li>Usual Provider:{" "}{t92}{t94}</li>;
        $[273] = t92;
        $[274] = t94;
        $[275] = t95;
      } else {
        t95 = $[275];
      }
      let t96;
      if ($[276] !== referral.referringProvider) {
        t96 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[276] = referral.referringProvider;
        $[277] = t96;
      } else {
        t96 = $[277];
      }
      let t97;
      if ($[278] !== referringProviderSpecialty) {
        t97 = <CopyableContent text={referringProviderSpecialty} />;
        $[278] = referringProviderSpecialty;
        $[279] = t97;
      } else {
        t97 = $[279];
      }
      let t98;
      if ($[280] !== t96 || $[281] !== t97) {
        t98 = <li>Referring Provider: {t96}, Specialty:{" "}{t97}</li>;
        $[280] = t96;
        $[281] = t97;
        $[282] = t98;
      } else {
        t98 = $[282];
      }
      let t99;
      if ($[283] !== isPrimaryCareProvider || $[284] !== referral.referringProvider) {
        t99 = isPrimaryCareProvider && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[283] = isPrimaryCareProvider;
        $[284] = referral.referringProvider;
        $[285] = t99;
      } else {
        t99 = $[285];
      }
      let t100;
      if ($[286] !== referral.referringProvider || $[287] !== referringLocationAddress) {
        t100 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[286] = referral.referringProvider;
        $[287] = referringLocationAddress;
        $[288] = t100;
      } else {
        t100 = $[288];
      }
      let t101;
      if ($[289] !== t100 || $[290] !== t95 || $[291] !== t98 || $[292] !== t99) {
        t101 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t95}{t98}{t99}{t100}</ul></li>;
        $[289] = t100;
        $[290] = t95;
        $[291] = t98;
        $[292] = t99;
        $[293] = t101;
      } else {
        t101 = $[293];
      }
      let t102;
      if ($[294] !== emergencyContactName) {
        t102 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[294] = emergencyContactName;
        $[295] = t102;
      } else {
        t102 = $[295];
      }
      let t103;
      if ($[296] !== emergencyContactPhoneNumber) {
        t103 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[296] = emergencyContactPhoneNumber;
        $[297] = t103;
      } else {
        t103 = $[297];
      }
      let t104;
      if ($[298] !== emergencyContactRelationship) {
        t104 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[298] = emergencyContactRelationship;
        $[299] = t104;
      } else {
        t104 = $[299];
      }
      let t105;
      if ($[300] !== t102 || $[301] !== t103 || $[302] !== t104) {
        t105 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t102}{t103}{t104}</ul></li>;
        $[300] = t102;
        $[301] = t103;
        $[302] = t104;
        $[303] = t105;
      } else {
        t105 = $[303];
      }
      const t106 = primaryCoverageSummary ?? "N/A";
      let t107;
      if ($[304] !== t106) {
        t107 = <li>Primary: <CopyableContent text={t106} /></li>;
        $[304] = t106;
        $[305] = t107;
      } else {
        t107 = $[305];
      }
      let t108;
      if ($[306] !== secondaryCoverageSummary) {
        t108 = secondaryCoverageSummary && <li>Secondary: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[306] = secondaryCoverageSummary;
        $[307] = t108;
      } else {
        t108 = $[307];
      }
      let t109;
      let t110;
      if ($[308] !== insuranceStatusDisplay) {
        t109 = <CopyableContent text={insuranceStatusDisplay} />;
        t110 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[308] = insuranceStatusDisplay;
        $[309] = t109;
        $[310] = t110;
      } else {
        t109 = $[309];
        t110 = $[310];
      }
      let t111;
      if ($[311] !== insuranceStatusDisplay) {
        t111 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[311] = insuranceStatusDisplay;
        $[312] = t111;
      } else {
        t111 = $[312];
      }
      let t112;
      if ($[313] !== t109 || $[314] !== t110 || $[315] !== t111) {
        t112 = <li>Eligibility check result: {t109}{t110}{t111}</li>;
        $[313] = t109;
        $[314] = t110;
        $[315] = t111;
        $[316] = t112;
      } else {
        t112 = $[316];
      }
      let t113;
      if ($[317] !== t107 || $[318] !== t108 || $[319] !== t112) {
        t113 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t107}{t108}{t112}</ul></li>;
        $[317] = t107;
        $[318] = t108;
        $[319] = t112;
        $[320] = t113;
      } else {
        t113 = $[320];
      }
      let t114;
      if ($[321] === Symbol.for("react.memo_cache_sentinel")) {
        t114 = <li>Call and text are YES</li>;
        $[321] = t114;
      } else {
        t114 = $[321];
      }
      let t115;
      if ($[322] !== privacyNoticeOnFile) {
        t115 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[322] = privacyNoticeOnFile;
        $[323] = t115;
      } else {
        t115 = $[323];
      }
      let t116;
      if ($[324] !== t115) {
        t116 = <li>Privacy is YES{t115}</li>;
        $[324] = t115;
        $[325] = t116;
      } else {
        t116 = $[325];
      }
      let t117;
      if ($[326] === Symbol.for("react.memo_cache_sentinel")) {
        t117 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[326] = t117;
      } else {
        t117 = $[326];
      }
      let t118;
      if ($[327] !== t116) {
        t118 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t114}{t116}{t117}</ul></li>;
        $[327] = t116;
        $[328] = t118;
      } else {
        t118 = $[328];
      }
      if ($[329] !== t101 || $[330] !== t105 || $[331] !== t113 || $[332] !== t118 || $[333] !== t89 || $[334] !== t90) {
        t22 = <div>{t88}<ol className="list-decimal list-inside space-y-3">{t89}{t90}{t91}{t101}{t105}{t113}{t118}</ol></div>;
        $[329] = t101;
        $[330] = t105;
        $[331] = t113;
        $[332] = t118;
        $[333] = t89;
        $[334] = t90;
        $[335] = t22;
      } else {
        t22 = $[335];
      }
      if ($[336] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[336] = t15;
      } else {
        t15 = $[336];
      }
      t11 = "list-decimal list-inside space-y-3";
      if ($[337] !== documentId) {
        t12 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[337] = documentId;
        $[338] = t12;
      } else {
        t12 = $[338];
      }
      let t119;
      if ($[339] !== referringProviderName) {
        t119 = <CopyableContent text={referringProviderName} />;
        $[339] = referringProviderName;
        $[340] = t119;
      } else {
        t119 = $[340];
      }
      const t120 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t121;
      if ($[341] !== t120) {
        t121 = <CopyableContent text={t120} />;
        $[341] = t120;
        $[342] = t121;
      } else {
        t121 = $[342];
      }
      let t122;
      if ($[343] !== referringLocationAddress) {
        t122 = <CopyableContent text={referringLocationAddress} />;
        $[343] = referringLocationAddress;
        $[344] = t122;
      } else {
        t122 = $[344];
      }
      if ($[345] !== t119 || $[346] !== t121 || $[347] !== t122) {
        t13 = <li>Referring provider: Name: {t119}, NPI:{" "}{t121}, Address:{" "}{t122}</li>;
        $[345] = t119;
        $[346] = t121;
        $[347] = t122;
        $[348] = t13;
      } else {
        t13 = $[348];
      }
      if ($[349] !== resolvedReceiving.providerLabel || $[350] !== resolvedReceiving.providerNpi || $[351] !== resolvedReceiving.providerWriteSafe) {
        t14 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[349] = resolvedReceiving.providerLabel;
        $[350] = resolvedReceiving.providerNpi;
        $[351] = resolvedReceiving.providerWriteSafe;
        $[352] = t14;
      } else {
        t14 = $[352];
      }
      t9 = "Patient Type: ";
      if ($[353] !== patientLabel) {
        t10 = <CopyableContent text={patientLabel} />;
        $[353] = patientLabel;
        $[354] = t10;
      } else {
        t10 = $[354];
      }
      t7 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t8 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp38);
    }
    $[12] = actionNote;
    $[13] = athenaData?.coverages;
    $[14] = athenaData?.patientDetails;
    $[15] = bucketLabel;
    $[16] = chartReferralAuths;
    $[17] = chartRequestLetters;
    $[18] = coverageReadback;
    $[19] = dateOfReferral;
    $[20] = decisionTrace;
    $[21] = documentId;
    $[22] = documentUploadLabels;
    $[23] = eligibilityDisplay;
    $[24] = emergencyContactName;
    $[25] = emergencyContactPhoneNumber;
    $[26] = emergencyContactRelationship;
    $[27] = followThroughEligible;
    $[28] = insuranceRequirements;
    $[29] = internalNoteToEnter;
    $[30] = isPrimaryCareProvider;
    $[31] = pastAppointments;
    $[32] = patient;
    $[33] = patientEhrId;
    $[34] = patientLabel;
    $[35] = pendingApproval;
    $[36] = primaryCareProviderAlreadyPresent;
    $[37] = primaryCoverageSummary;
    $[38] = privacyNoticeOnFile;
    $[39] = receivingAssignmentConfirmed;
    $[40] = receivingProviderName;
    $[41] = referral;
    $[42] = referralAuthorization;
    $[43] = referralSourceOnFile;
    $[44] = referringLocationAddress;
    $[45] = referringProviderName;
    $[46] = referringProviderSpecialty;
    $[47] = resolvedReceiving;
    $[48] = secondaryCoverageSummary;
    $[49] = task;
    $[50] = taskSteps;
    $[51] = unresolvedUpcomingProviders;
    $[52] = upcomingAppointmentProviderSummary;
    $[53] = upcomingAppointments;
    $[54] = urgency;
    $[55] = view;
    $[56] = T0;
    $[57] = dataSourceEntries;
    $[58] = hasMissingInformation;
    $[59] = missingFields;
    $[60] = t10;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
  } else {
    T0 = $[56];
    dataSourceEntries = $[57];
    hasMissingInformation = $[58];
    missingFields = $[59];
    t10 = $[60];
    t11 = $[61];
    t12 = $[62];
    t13 = $[63];
    t14 = $[64];
    t15 = $[65];
    t16 = $[66];
    t17 = $[67];
    t18 = $[68];
    t19 = $[69];
    t20 = $[70];
    t21 = $[71];
    t22 = $[72];
    t23 = $[73];
    t24 = $[74];
    t25 = $[75];
    t7 = $[76];
    t8 = $[77];
    t9 = $[78];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[355] !== t7 || $[356] !== t8) {
    t26 = <ul className={t7}>{t8}</ul>;
    $[355] = t7;
    $[356] = t8;
    $[357] = t26;
  } else {
    t26 = $[357];
  }
  let t27;
  if ($[358] !== t10 || $[359] !== t26 || $[360] !== t9) {
    t27 = <li>{t9}{t10}{t26}</li>;
    $[358] = t10;
    $[359] = t26;
    $[360] = t9;
    $[361] = t27;
  } else {
    t27 = $[361];
  }
  let t28;
  if ($[362] !== dateOfReferral) {
    t28 = <li>Date: <CopyableContent text={dateOfReferral} /></li>;
    $[362] = dateOfReferral;
    $[363] = t28;
  } else {
    t28 = $[363];
  }
  let t29;
  if ($[364] !== internalNoteToEnter) {
    t29 = <CopyableContent text={internalNoteToEnter} />;
    $[364] = internalNoteToEnter;
    $[365] = t29;
  } else {
    t29 = $[365];
  }
  let t30;
  if ($[366] !== flags) {
    t30 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp39)}</ul>;
    $[366] = flags;
    $[367] = t30;
  } else {
    t30 = $[367];
  }
  let t31;
  if ($[368] !== t29 || $[369] !== t30) {
    t31 = <li>Internal Note: {t29}{t30}</li>;
    $[368] = t29;
    $[369] = t30;
    $[370] = t31;
  } else {
    t31 = $[370];
  }
  let t32;
  if ($[371] !== schedulingPriority) {
    t32 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[371] = schedulingPriority;
    $[372] = t32;
  } else {
    t32 = $[372];
  }
  let t33;
  if ($[373] !== bucketLabel) {
    t33 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[373] = bucketLabel;
    $[374] = t33;
  } else {
    t33 = $[374];
  }
  let t34;
  if ($[375] !== bucketLabel || $[376] !== decisionTrace) {
    t34 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp40)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[375] = bucketLabel;
    $[376] = decisionTrace;
    $[377] = t34;
  } else {
    t34 = $[377];
  }
  let t35;
  if ($[378] !== t33 || $[379] !== t34) {
    t35 = <li>{t33}{t34}</li>;
    $[378] = t33;
    $[379] = t34;
    $[380] = t35;
  } else {
    t35 = $[380];
  }
  let t36;
  if ($[381] !== actionNote) {
    t36 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[381] = actionNote;
    $[382] = t36;
  } else {
    t36 = $[382];
  }
  let t37;
  if ($[383] !== internalNoteToEnter) {
    t37 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} /></li>;
    $[383] = internalNoteToEnter;
    $[384] = t37;
  } else {
    t37 = $[384];
  }
  let t38;
  if ($[385] !== t11 || $[386] !== t12 || $[387] !== t13 || $[388] !== t14 || $[389] !== t27 || $[390] !== t28 || $[391] !== t31 || $[392] !== t32 || $[393] !== t35 || $[394] !== t36 || $[395] !== t37) {
    t38 = <ol className={t11}>{t12}{t13}{t14}{t27}{t28}{t31}{t32}{t35}{t36}{t37}</ol>;
    $[385] = t11;
    $[386] = t12;
    $[387] = t13;
    $[388] = t14;
    $[389] = t27;
    $[390] = t28;
    $[391] = t31;
    $[392] = t32;
    $[393] = t35;
    $[394] = t36;
    $[395] = t37;
    $[396] = t38;
  } else {
    t38 = $[396];
  }
  let t39;
  if ($[397] !== t15 || $[398] !== t38) {
    t39 = <div>{t15}{t38}</div>;
    $[397] = t15;
    $[398] = t38;
    $[399] = t39;
  } else {
    t39 = $[399];
  }
  let t40;
  let t41;
  if ($[400] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t41 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[400] = t40;
    $[401] = t41;
  } else {
    t40 = $[400];
    t41 = $[401];
  }
  let t42;
  if ($[402] !== documentUploadLabels) {
    t42 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp41);
    $[402] = documentUploadLabels;
    $[403] = t42;
  } else {
    t42 = $[403];
  }
  let t43;
  if ($[404] !== t42) {
    t43 = <div>{t40}{t41}<ul className="ml-4 space-y-1 list-disc list-inside">{t42}</ul></div>;
    $[404] = t42;
    $[405] = t43;
  } else {
    t43 = $[405];
  }
  let t44;
  let t45;
  if ($[406] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t45 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[406] = t44;
    $[407] = t45;
  } else {
    t44 = $[406];
    t45 = $[407];
  }
  let t46;
  if ($[408] !== hasMissingInformation || $[409] !== missingFields) {
    t46 = hasMissingInformation ? `Missing: ${missingFields.map(_temp42).join(", ")}` : "No missing-information letter needed.";
    $[408] = hasMissingInformation;
    $[409] = missingFields;
    $[410] = t46;
  } else {
    t46 = $[410];
  }
  let t47;
  if ($[411] !== t46) {
    t47 = <p className="mb-3">{t46}</p>;
    $[411] = t46;
    $[412] = t47;
  } else {
    t47 = $[412];
  }
  let t48;
  if ($[413] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[413] = t48;
  } else {
    t48 = $[413];
  }
  let t49;
  if ($[414] !== dataSourceEntries) {
    t49 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp43)}</ul>;
    $[414] = dataSourceEntries;
    $[415] = t49;
  } else {
    t49 = $[415];
  }
  let t50;
  if ($[416] !== dateOfReferral || $[417] !== hasMissingInformation || $[418] !== referringProviderName) {
    t50 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateOfReferral} />"</li></ul></li></ul></li></ol>;
    $[416] = dateOfReferral;
    $[417] = hasMissingInformation;
    $[418] = referringProviderName;
    $[419] = t50;
  } else {
    t50 = $[419];
  }
  let t51;
  if ($[420] !== t47 || $[421] !== t49 || $[422] !== t50) {
    t51 = <div>{t44}{t45}{t47}{t48}{t49}{t50}</div>;
    $[420] = t47;
    $[421] = t49;
    $[422] = t50;
    $[423] = t51;
  } else {
    t51 = $[423];
  }
  let t52;
  if ($[424] !== t16 || $[425] !== t17 || $[426] !== t18 || $[427] !== t19 || $[428] !== t20 || $[429] !== t21 || $[430] !== t22 || $[431] !== t39 || $[432] !== t43 || $[433] !== t51) {
    t52 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t39}{t43}{t51}</div>;
    $[424] = t16;
    $[425] = t17;
    $[426] = t18;
    $[427] = t19;
    $[428] = t20;
    $[429] = t21;
    $[430] = t22;
    $[431] = t39;
    $[432] = t43;
    $[433] = t51;
    $[434] = t52;
  } else {
    t52 = $[434];
  }
  let t53;
  if ($[435] !== T0 || $[436] !== t23 || $[437] !== t52) {
    t53 = <T0 collapsibleTitle={t23}>{t52}</T0>;
    $[435] = T0;
    $[436] = t23;
    $[437] = t52;
    $[438] = t53;
  } else {
    t53 = $[438];
  }
  let t54;
  if ($[439] !== referral.id || $[440] !== task.id) {
    t54 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[439] = referral.id;
    $[440] = task.id;
    $[441] = t54;
  } else {
    t54 = $[441];
  }
  let t55;
  if ($[442] !== athenaData || $[443] !== referral) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[442] = athenaData;
    $[443] = referral;
    $[444] = t55;
  } else {
    t55 = $[444];
  }
  let t56;
  if ($[445] !== t24 || $[446] !== t53 || $[447] !== t54 || $[448] !== t55) {
    t56 = <div className={t24}>{t53}{t54}{t55}</div>;
    $[445] = t24;
    $[446] = t53;
    $[447] = t54;
    $[448] = t55;
    $[449] = t56;
  } else {
    t56 = $[449];
  }
  return t56;
}
function _temp43(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp42(entry_1) {
  return entry_1.label;
}
function _temp41(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp40(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp39(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp38(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp37(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp36(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp35(coverage_0) {
  return coverage_0.order === 2;
}
function _temp34(coverage) {
  return coverage.order === 1;
}
function _temp33(a_0, b_0) {
  return (a_0.order ?? 99) - (b_0.order ?? 99);
}
function _temp32(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp31(value) {
  return Boolean(value);
}
function _temp30(item) {
  return item.diagnosisText;
}
function _temp29(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}